import React, { useState } from 'react'
import { Message } from 'primereact/message'
import { Card } from 'primereact/card'
import { Badge } from 'primereact/badge'
import { Skeleton } from 'primereact/skeleton'
import { ProgressBar } from 'primereact/progressbar'
import { TabView, TabPanel } from 'primereact/tabview'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Panel } from 'primereact/panel'
import { Button } from 'primereact/button'
import { useOrderData } from '../../../hooks/useOrderData'
import { useOrderSelection } from '../../../hooks/useOrderSelection'
import { useLineItems } from '../../../hooks/useLineItems'
import { useLineItemSelection } from '../../../hooks/useLineItemSelection'
import { ViewOrderInfoDialog } from '../ViewOrderInfoDialog'
import { ShippingOrder } from '../../../DeShippingTableWrapper'

type ProcessingState = 'idle' | 'processing' | 'completed' | 'error'

// New API Response Interfaces
interface ApiSummary {
  total_orders_processed: number
  total_declined_items_found: number
  successful_reassignments: number
  failed_reassignments: number
  full_fulfillment_orders: number
  partial_fulfillment_orders: number
  rejected_orders: number
  execution_time_ms: number
}

interface UnfulfillableItem {
  line_item_id: string
  product_sku: string
  product_name: string
  quantity_needed: number
  max_available_across_stores: number
  shortage: number
  current_status: string
  previous_location: string
}

interface FulfillmentItem {
  line_item_id: string
  product_sku: string
  product_name: string
  quantity_needed: number
  assigned_store: string
  current_status: string
  previous_location: string
}

interface RejectedOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  reason: string
  unfulfillable_items: UnfulfillableItem[]
}

interface FullFulfillmentOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  assigned_store: string
  items: FulfillmentItem[]
}

interface PartialFulfillmentOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  fulfilled_items: FulfillmentItem[]
  unfulfillable_items: UnfulfillableItem[]
}

interface ApiResults {
  full_store_fulfillment: FullFulfillmentOrder[]
  partial_store_fulfillment: PartialFulfillmentOrder[]
  rejected_orders: RejectedOrder[]
}

interface ApiResponse {
  success: boolean
  summary: ApiSummary
  results: ApiResults
}

// Component display interfaces
interface DisplayItem {
  id: string
  orderId: string
  orderName: string
  sku: string
  productName: string
  quantity: number
  originalStore: string
  assignedStore?: string
  status: 'completed' | 'failed'
  reason?: string
  shortage?: number
  maxAvailable?: number
}

interface RejectedItemsResultsViewProps {
  orderData: ReturnType<typeof useOrderData>
  orderSelection: ReturnType<typeof useOrderSelection>
  lineItemsHook: ReturnType<typeof useLineItems>
  lineItemSelection: ReturnType<typeof useLineItemSelection>
  processingState: ProcessingState
  apiResponse: ApiResponse | null
}

export const RejectedItemsResultsView: React.FC<RejectedItemsResultsViewProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection,
  processingState,
  apiResponse
}) => {
  const [isOrderDialogVisible, setIsOrderDialogVisible] = useState(false)

  // Transform API data into display format
  const transformedData = React.useMemo(() => {
    if (!apiResponse) {
      return { completedItems: [], failedItems: [] }
    }

    const completedItems: DisplayItem[] = []
    const failedItems: DisplayItem[] = []

    // Process full fulfillment orders (completed)
    apiResponse.results.full_store_fulfillment.forEach(order => {
      order.items.forEach(item => {
        completedItems.push({
          id: item.line_item_id,
          orderId: order.order_id,
          orderName: order.order_name,
          sku: item.product_sku,
          productName: item.product_name,
          quantity: item.quantity_needed,
          originalStore: item.previous_location,
          assignedStore: item.assigned_store,
          status: 'completed'
        })
      })
    })

    // Process partial fulfillment orders (mix of completed and failed)
    apiResponse.results.partial_store_fulfillment.forEach(order => {
      // Add completed items
      order.fulfilled_items.forEach(item => {
        completedItems.push({
          id: item.line_item_id,
          orderId: order.order_id,
          orderName: order.order_name,
          sku: item.product_sku,
          productName: item.product_name,
          quantity: item.quantity_needed,
          originalStore: item.previous_location,
          assignedStore: item.assigned_store,
          status: 'completed'
        })
      })

      // Add failed items
      order.unfulfillable_items.forEach(item => {
        failedItems.push({
          id: item.line_item_id,
          orderId: order.order_id,
          orderName: order.order_name,
          sku: item.product_sku,
          productName: item.product_name,
          quantity: item.quantity_needed,
          originalStore: item.previous_location,
          status: 'failed',
          reason: `Insufficient inventory (Available: ${item.max_available_across_stores}, Needed: ${item.quantity_needed})`,
          shortage: item.shortage,
          maxAvailable: item.max_available_across_stores
        })
      })
    })

    // Process rejected orders (all failed)
    apiResponse.results.rejected_orders.forEach(order => {
      order.unfulfillable_items.forEach(item => {
        failedItems.push({
          id: item.line_item_id,
          orderId: order.order_id,
          orderName: order.order_name,
          sku: item.product_sku,
          productName: item.product_name,
          quantity: item.quantity_needed,
          originalStore: item.previous_location,
          status: 'failed',
          reason: order.reason,
          shortage: item.shortage,
          maxAvailable: item.max_available_across_stores
        })
      })
    })

    return { completedItems, failedItems }
  }, [apiResponse])

  // Auto-switch to Failed tab if no completed items
  const [activeIndex, setActiveIndex] = useState(
    transformedData.completedItems.length === 0 && transformedData.failedItems.length > 0 ? 1 : 0
  )

  // Handler for viewing order details
  const handleViewOrderDetails = (item: DisplayItem) => {
    // Find the order for this item and set it as selected
    const selectedOrders = orderSelection.getSelectedOrdersData()
    const order = selectedOrders.find(o => o.order_id === item.orderId)
    if (order) {
      orderSelection.selectOrder(order)
      setIsOrderDialogVisible(true)
    }
  }

  // Actions template for DataTable
  const actionsTemplate = (rowData: DisplayItem) => (
    <Button
      label="Details"
      icon="pi pi-eye"
      size="small"
      className="p-button-outlined p-button-sm"
      onClick={() => handleViewOrderDetails(rowData)}
    />
  )

  // Loading State
  if (processingState === 'processing') {
    return (
      <div className="flex flex-column gap-4">
        <Message
          severity="info"
          content={
            <div className="flex align-items-center gap-2">
              <i className="pi pi-spin pi-spinner" style={{ marginRight: '0.5rem' }}></i>
              <span>Processing bulk reassignment... Please wait while we reassign items to optimal stores.</span>
            </div>
          }
          className="w-full"
        />
        
        {/* Progress Bar */}
        <Card className="w-full">
          <div className="text-center mb-3">
            <h6 className="mb-3">Reassigning Items...</h6>
            <ProgressBar mode="indeterminate" style={{ height: '8px' }} className="mb-3" />
            <div className="text-sm text-600">
              Analyzing inventory levels and optimizing store assignments
            </div>
          </div>
        </Card>

        {/* Loading Skeletons */}
        <Card title="Processing Summary" className="w-full">
          <div className="grid">
            <div className="col-6">
              <div className="flex align-items-center gap-3 p-3 surface-100 border-round">
                <Skeleton shape="circle" size="3rem" />
                <div className="flex-1">
                  <Skeleton width="4rem" height="1.5rem" className="mb-2" />
                  <Skeleton width="8rem" height="1rem" />
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="flex align-items-center gap-3 p-3 surface-100 border-round">
                <Skeleton shape="circle" size="3rem" />
                <div className="flex-1">
                  <Skeleton width="4rem" height="1.5rem" className="mb-2" />
                  <Skeleton width="8rem" height="1rem" />
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="flex align-items-center gap-3 p-3 surface-100 border-round">
                <Skeleton shape="circle" size="3rem" />
                <div className="flex-1">
                  <Skeleton width="6rem" height="1.5rem" className="mb-2" />
                  <Skeleton width="8rem" height="1rem" />
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="flex align-items-center gap-3 p-3 surface-100 border-round">
                <Skeleton shape="circle" size="3rem" />
                <div className="flex-1">
                  <Skeleton width="5rem" height="1.5rem" className="mb-2" />
                  <Skeleton width="8rem" height="1rem" />
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Processing Steps */}
        <Card title="Progress" className="w-full">
          <div className="flex flex-column gap-3">
            <div className="flex align-items-center gap-3 p-2">
              <i className="pi pi-check-circle text-green-500"></i>
              <span>Analyzing rejected items...</span>
              <Badge value="Complete" severity="success" />
            </div>
            <div className="flex align-items-center gap-3 p-2">
              <i className="pi pi-spin pi-spinner text-blue-500"></i>
              <span>Finding optimal store assignments...</span>
              <Badge value="In Progress" severity="info" />
            </div>
            <div className="flex align-items-center gap-3 p-2">
              <i className="pi pi-clock text-500"></i>
              <span>Updating inventory systems...</span>
              <Badge value="Pending" severity="secondary" />
            </div>
            <div className="flex align-items-center gap-3 p-2">
              <i className="pi pi-clock text-500"></i>
              <span>Notifying target stores...</span>
              <Badge value="Pending" severity="secondary" />
            </div>
          </div>
        </Card>
      </div>
    )
  }

  // Error State
  if (processingState === 'error') {
    return (
      <div className="flex flex-column gap-4">
        <Message
          severity="error"
          text="Bulk reassignment failed. Please try again or contact support if the issue persists."
          className="w-full"
        />
        
        <Card title="Error Details" className="w-full">
          <div className="text-center">
            <i className="pi pi-times-circle" style={{ fontSize: '3rem', color: 'var(--red-500)' }}></i>
            <h5 className="mt-3 mb-2 text-red-700">Reassignment Failed</h5>
            <p className="text-600 mb-4">
              An error occurred while processing the bulk reassignment. 
              No orders have been modified.
            </p>
          </div>
        </Card>
      </div>
    )
  }

  // Results State (shown for both success and failure)
  const { completedItems, failedItems } = transformedData
  const summary = apiResponse?.summary

  return (
    <div className="flex flex-column gap-4">
      <Message
        severity={apiResponse?.success ? "success" : "error"}
        content={
          <div className="flex align-items-center gap-2">
            <i className={`pi ${apiResponse?.success ? 'pi-check-circle' : 'pi-times-circle'}`} style={{ fontSize: '1.25rem' }}></i>
            <span>
              {apiResponse?.success 
                ? `Bulk reassignment completed${failedItems.length > 0 ? ' with some failures' : ' successfully'}.`
                : 'Bulk reassignment failed. All items could not be fulfilled due to inventory constraints.'
              }
            </span>
          </div>
        }
        className="w-full"
      />
      
      {/* Success Summary Cards */}
      <div 
        className="p-3 mb-4" 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          alignItems: 'center',
          width: '100%',
          gap: '16px'
        }}
      >
        <Card style={{ 
          textAlign: 'center', 
          flex: '1',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '0',
          color: 'white',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
          border: 'none'
        }} className="p-0">
          <div style={{ padding: '12px 16px' }}>
            <div style={{ marginBottom: '4px' }}>
              <i className="pi pi-shopping-cart" style={{ fontSize: '1.6rem' }}></i>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
              {summary?.total_orders_processed || 0}
            </div>
            <div style={{ fontSize: '0.85rem', opacity: '0.9' }}>
              Orders Processed
            </div>
          </div>
        </Card>

        <Card style={{ 
          textAlign: 'center', 
          flex: '1',
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          borderRadius: '12px',
          padding: '0',
          color: 'white',
          boxShadow: '0 4px 15px rgba(79, 172, 254, 0.3)',
          border: 'none'
        }} className="p-0">
          <div style={{ padding: '12px 16px' }}>
            <div style={{ marginBottom: '4px' }}>
              <i className="pi pi-check-circle" style={{ fontSize: '1.6rem' }}></i>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
              {summary?.successful_reassignments || 0}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>
              Successful Reassignments
            </div>
          </div>
        </Card>

        <Card style={{ 
          textAlign: 'center', 
          flex: '1',
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderRadius: '12px',
          padding: '0',
          color: 'white',
          boxShadow: '0 4px 15px rgba(240, 147, 251, 0.3)',
          border: 'none'
        }} className="p-0">
          <div style={{ padding: '12px 16px' }}>
            <div style={{ marginBottom: '4px' }}>
              <i className="pi pi-times-circle" style={{ fontSize: '1.6rem' }}></i>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
              {summary?.total_declined_items_found || 0}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>
              Items Declined
            </div>
          </div>
        </Card>

        <Card style={{ 
          textAlign: 'center', 
          flex: '1',
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          borderRadius: '12px',
          padding: '0',
          color: 'white',
          boxShadow: '0 4px 15px rgba(250, 112, 154, 0.3)',
          border: 'none'
        }} className="p-0">
          <div style={{ padding: '12px 16px' }}>
            <div style={{ marginBottom: '4px' }}>
              <i className="pi pi-percentage" style={{ fontSize: '1.6rem' }}></i>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
              {summary ? Math.round(((summary.successful_reassignments) / (summary.total_declined_items_found || 1)) * 100) : 0}%
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>
              Success Rate
            </div>
          </div>
        </Card>
      </div>

      {/* Tabbed Results */}
      <Card title="Detailed Results" className="w-full">
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel 
            header={
              <div className="flex align-items-center gap-2">
                <i className="pi pi-check-circle text-green-600"></i>
                <span>Completed ({completedItems.length})</span>
              </div>
            }
          >
            <DataTable 
              value={completedItems} 
              size="small"
              stripedRows
              showGridlines
              emptyMessage="No completed items"
            >
              <Column field="orderId" header="Order ID" sortable style={{ width: '120px' }} />
              <Column field="orderName" header="Order Name" sortable style={{ width: '100px' }} />
              <Column field="productName" header="Product" sortable />
              <Column field="quantity" header="Qty" sortable style={{ width: '60px' }} className="text-center" />
              <Column field="assignedStore" header="Assigned Store" sortable style={{ width: '150px' }} />
              <Column 
                header="Status" 
                style={{ width: '80px' }}
                body={() => <Badge value="Success" severity="success" />}
              />
              <Column 
                header="Actions" 
                body={actionsTemplate}
                style={{ width: '100px' }}
              />
            </DataTable>
          </TabPanel>
          
          <TabPanel 
            header={
              <div className="flex align-items-center gap-2">
                <i className="pi pi-times-circle text-red-600"></i>
                <span>Failed ({failedItems.length})</span>
              </div>
            }
          >
            <DataTable 
              value={failedItems} 
              size="small"
              stripedRows
              showGridlines
              emptyMessage="No failed items"
            >
              <Column field="orderId" header="Order ID" sortable style={{ width: '120px' }} />
              <Column field="orderName" header="Order Name" sortable style={{ width: '100px' }} />
              <Column field="productName" header="Product" sortable />
              <Column field="quantity" header="Qty" sortable style={{ width: '60px' }} className="text-center" />
              <Column 
                field="maxAvailable" 
                header="Available" 
                sortable 
                style={{ width: '80px' }} 
                className="text-center"
                body={(rowData) => (
                  <Badge 
                    value={rowData.maxAvailable || 0} 
                    severity={rowData.maxAvailable === 0 ? "danger" : "warning"}
                  />
                )}
              />
              <Column field="originalStore" header="Previous Store" sortable style={{ width: '150px' }} />
              <Column 
                field="reason" 
                header="Failure Reason" 
                sortable 
                style={{ width: '250px' }}
                body={(rowData) => (
                  <span className="text-red-600 font-medium">{rowData.reason}</span>
                )}
              />
              <Column 
                header="Actions" 
                body={actionsTemplate}
                style={{ width: '100px' }}
              />
            </DataTable>
          </TabPanel>
        </TabView>
      </Card>

      {/* ViewOrderInfoDialog */}
      <ViewOrderInfoDialog
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
        visible={isOrderDialogVisible}
        onHide={() => setIsOrderDialogVisible(false)}
      />
    </div>
  )
} 